<template>
  <div class="edit-sections-content">
    <content-spinner :show="isLoading" />
    <show-error
      :show="showError"
      :text="modalError"
    />
    <template v-if="!isLoading && modalData">
      <ValidationObserver ref="observer">
        <designer-grid-layout
          v-for="section of modalData.Sections"
          :key="section.Name"
          :styles="section.styles"
          :is-placeholder="section.Placeholder"
          :is-hidden="section.isHidden"
          :title="section.Name"
          :layout="section.Fields"
          :is-file-previewer="isFilePreviewer"
        />
        <unrelated-fields
          :is-file-previewer="isFilePreviewer"
        />
      </ValidationObserver>
    </template>
  </div>
</template>

<script>
import {createNamespacedHelpers} from 'vuex';
import DesignerGridLayout from '@/core/components/complex-table/DesignerGridLayout.vue';
import UnrelatedFields from '@/core/components/complex-table/UnrelatedFields.vue';
import ContentSpinner from '@/core/components/ContentSpinner.vue';
import ShowError from '@/core/components/ShowError.vue';
import moment from 'moment';

const {
  mapState: dataManagementMapState,
  mapActions: dataManagementMapActions,
} = createNamespacedHelpers('dataManagement');

const {
  mapState: designerMapState,
  mapActions: designerMapActions,
} = createNamespacedHelpers('designer');

const {
  mapState: sidebarMapState,
} = createNamespacedHelpers('sidebar');

const DATE_FORMAT = 'YYYY/MM/DD';
const DATETIME_FORMAT = 'YYYY/MM/DD HH:mm:ss';

export default {
  name: 'EditSectionsContent',
  components: {
    ShowError,
    ContentSpinner,
    UnrelatedFields,
    DesignerGridLayout,
  },
  props: {
    isEmbedded: {
      type: Boolean,
      default: false,
    },
    isFilePreviewer: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isLoading: false,
      width: null,
      height: null,
      layout: null,
      formData: null,
      defaultFormBackup: null,
      showError: false,
      modalError: '',
      updated: null,
    };
  },
  computed: {
    ...designerMapState({
      modalState: state => state,
      fields: state => state.fields,
      modalData: state => state.data,
      isEditMode: state => state.isEditMode,
      defaultForm: state => state.form,
      primaryKey: state => state.primaryKey,
      dataInEditMode: state => state.item,
    }),
    ...dataManagementMapState({
      validation: state => state.validation,
    }),
    ...sidebarMapState({
      tablesList: state => state.tablesList,
    }),
  },
  methods: {
    ...designerMapActions([
      'setEditRowSectionsModalState',
      'resetEditRowForm',
    ]),
    ...dataManagementMapActions([
      'addNewRecord',
      'updateTableItems',
    ]),
    getDateFormat(field) {
      const jsonData = typeof field.dataTypeJSON === 'string'
        ? JSON.parse(field.dataTypeJSON)
        : field.dataTypeJSON;
      const isDateField = field.dataTypeName === 'date';
      const format = jsonData.FormatString ? jsonData.FormatString : 'YYYY-MM-DD';

      return `${format}${isDateField ? '' : ' HH:mm:ss'}`;
    },
    /**
     * Handle form submission - validate and store Row in Table.
     * @param isConfirmation Flag to indicate iterative call after user confirmation below (Validated -> Confirmed).
     * @param uuid Record GUID passed in iterative call after user confirmation (as per isConfirmation).
     */
    async save(isConfirmation = false, uuid) {
      this.showError = false;
      if (!isConfirmation && !await this.$refs.observer.validate()) {
        return { success: false, error: 'Validation failed' };
      }
      const tableId = this.$route.params.tableId;
      this.isLoading = true;
      try {
        const currentData = this.isEditMode ? this.dataInEditMode : this.defaultForm;
        
        const map = {};

        Object.values(this.fields).forEach(field => {
          if (field.dataTypeName === 'lookup') {
            map[field.name] = field;
          }
          if (field.dataTypeName === 'date') {
            map[field.name] = field;
          }
          if (field.dataTypeName === 'dateTime') {
            map[field.name] = field;
          }
        });
        for (const key in currentData) {
          if (key in map) {
            if (map[key].dataTypeName === 'date') {
              currentData[key] = !currentData[key] ? null : moment(currentData[key], this.getDateFormat(map[key])).format(DATE_FORMAT);
            }
            if (map[key].dataTypeName === 'dateTime') {
              currentData[key] = !currentData[key] ? null : moment(currentData[key], this.getDateFormat(map[key])).format(DATETIME_FORMAT);
            }
            if (map[key].dataTypeName === 'lookup') {
              currentData[key] = !currentData[key] ? null : currentData[key].id;
            }
          }
        }

        const data = this.updated || currentData;
        if (isConfirmation) {
          data[this.validation.fieldName] = this.validation.confirmed;
          data[`${this.tablesList[tableId].label}Guid`] = uuid;
        }

        // Strip out non-writeable fields
        const fieldPermissions = {};
        Object.values(this.fields).forEach(f => {
          fieldPermissions[f.name] = f.currentUserPermissionLevel;
        });
        Object.keys(data).forEach(k => {
          if (['read', 'none', 'deny'].includes(fieldPermissions[k])) {
            delete data[k];
          }
        });

        const params = {
          data: [data],
          primaryKey: this.primaryKey,
          tableId,
        };

        const {success, body, updatedRecord} = await this.addNewRecord(params);
        
        if (body.errors.length > 0) {
          this.isLoading = false;
          this.showErrorContent(body.errors);
          return { success: false, error: 'API errors', errors: body.errors };
        }
        if (success) {
          const resultParams = {
            insertData: {
              ...body,
              updatedRecord,
            },
          };
          
          // Emit the event for parent components to handle
          this.$emit('changed-row', resultParams);
          
          /*
            Only assign this.updated if updatedRecord is not an empty object.
            This prevents issues where the API returns an empty object (instead of null) for unchanged data,
            which could cause the modal to remain open and subsequent actions to use empty data.
          */
          if (JSON.stringify(updatedRecord) !== '{}') {
            this.updated = updatedRecord;
          }
          
          if ((updatedRecord[this.validation.status] === this.validation.validated) && !isConfirmation) {
            const confirmResult = await this.$confirm(
              'Confirmed Row',
              'Would you like to change \'Validated\' row to \'Confirmed\'?',
            );
            if (confirmResult) {
              return await this.save(true, body.resultSetTemp[0].recordGuid);
            }
          }
          
          const resData = body.resultSetTemp[0];
          if (resData) {
            if (resData['uploadStatus'] === 'rejected') {
              this.showErrorContent(resData['validationErrors']);
              return { success: false, error: 'Upload rejected', errors: resData['validationErrors'] };
            } else {
              this.resetEditRowForm(this.defaultFormBackup);
              this.$toasted.success('Record was saved successfully', {
                duration: 3000,
              });
              
              // For embedded usage, don't close the modal state automatically
              if (!this.isEmbedded) {
                this.setEditRowSectionsModalState({
                  show: false,
                  isEditMode: false,
                  item: null,
                });
              }
              
              return { success: true, data: resultParams };
            }
          }
        } else {
          this.$toasted.error('Error', {
            duration: 3000,
          });
          return { success: false, error: 'Save failed' };
        }
      } finally {
        this.isLoading = false;
      }
    },
    
    // Legacy method name for compatibility with existing modal usage
    async onSend(_, isConfirmation = false, uuid) {
      return await this.save(isConfirmation, uuid);
    },
    
    cancel() {
      this.resetEditRowForm(this.defaultFormBackup);
      if (!this.isEmbedded) {
        this.setEditRowSectionsModalState({
          show: false,
          isEditMode: false,
          item: null,
        });
      }
      this.$emit('cancelled');
    },
    
    showErrorContent(errors) {
      this.showError = true;
      this.modalError = errors.map(error => {
        let parsedError;

        try {
          parsedError = JSON.parse(error);
        } catch (err) {
          parsedError = error;
        }

        if (typeof parsedError === 'object') {
          return parsedError.Message;
        } else {
          return parsedError;
        }
      }).join(' ');
    },
  },
  beforeDestroy() {
    this.resetEditRowForm(this.defaultFormBackup);
  },
  created() {
    if (this.modalData?.ModalHeight && this.modalData?.ModalWidth) {
      document.body.style.setProperty('--height', this.modalData.ModalHeight);
      document.body.style.setProperty('--width', this.modalData.ModalWidth);
    }
  },
  beforeMount() {
    if (this.defaultForm) {
      this.defaultFormBackup = JSON.parse(JSON.stringify(this.defaultForm));
    }
  },
};
</script>
